import { Alert, Platform } from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import {
  ElementRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { startActivity, useDispatch, useSelector } from '@betty/toolkit/state';
import { login, selectLoginDataState } from '../state/auth/auth.slice';
import {
  StackActions,
  StackNavigationProp,
  useNavigation,
  Screen,
  useTransitionEnd,
  BottomSheet,
} from '@betty/navigation';
import { AuthNavigatorParamsList } from '../navigators/AuthNavigator/AuthNavigator.types';
import { ApiError } from '@betty/toolkit/api';
import {
  Box,
  Text,
  Input,
  Button,
  Checkbox,
  KeyboardSpacer,
  BettyIgoLogo,
} from '@betty/moonlight';
import { hasKey, getKey, Trans } from '@betty/i18n';
import Config from '@betty/toolkit/config';

import { Recaptcha, RecaptchaRef } from '../components';
import { LoginRejectedPayload } from '../state/auth/auth.slice.types';
import { NativeModules } from 'react-native';

function LoginScreen() {
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const emailRef = useRef<ElementRef<typeof Input>>(null);
  const passwordRef = useRef<ElementRef<typeof Input>>(null);
  const dispatch = useDispatch();
  const loginDataState = useSelector(selectLoginDataState);
  const navigation =
    useNavigation<StackNavigationProp<AuthNavigatorParamsList>>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEligible, setIsEligible] = useState(false);
  const [showError, setShowError] = useState(false);
  const [prefilledEmail, setPrefilledEmail] = useState<string>();
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [error, setError] = useState('');
  const mfaParams = useRef<LoginRejectedPayload | null>(null);
  const transitionEnded = useTransitionEnd();

  const eligibilityComponents = useMemo(
    () => ({
      textLink: (
        <Text
          variant="link-small"
          onPress={() =>
            BottomSheet.open({
              namespace: 'common',
              type: 'Info',
              data: {
                textNS: 'auth',
                textKey: 'eligibilityDescription',
              },
            })
          }
        />
      ),
    }),
    [],
  );

  const onLoginPress = useCallback(
    async (acceptTc: boolean = false) => {
      if (!isEligible || !email || !password) {
        if (!showError) {
          setShowError(true);
        }

        return;
      }

      if (error) {
        setError('');
      }

      if (mfaParams.current?.email === email) {
        navigation.navigate('MFAScreen', mfaParams.current);

        return;
      }

      setRecaptchaLoading(true);

      const googleRecaptchaToken = await recaptchaRef.current?.getToken();

      if (
        Platform.OS === 'android' &&
        Config.ENVIRONMENT === 'qa' &&
        googleRecaptchaToken?.length &&
        googleRecaptchaToken.length > 10000
      ) {
        Alert.alert(
          'Big recaptcha body',
          'We detected a big recaptcha string. Please paste the clipboard and send it to a developer.',
        );

        Clipboard.setString(googleRecaptchaToken);
      }

      const result = await dispatch(
        login({ email, password, acceptTc, googleRecaptchaToken }),
      );

      setRecaptchaLoading(false);

      if (login.rejected.match(result)) {
        if (result.payload instanceof ApiError) {
          const { errorCode, errorMessages } = result.payload?.data || {};

          // TODO: Replace with proper error code once BE is updated
          if (errorMessages?.[0] === 'Terms and conditions not accepted.') {
            BottomSheet.open({
              namespace: 'auth',
              type: 'TermsAndConditions',
              data: {
                confirm: () => onLoginPress(true),
              },
            });
            return;
          }

          let errorMessage = '';

          if (errorCode && hasKey(`auth.errors.${errorCode}`)) {
            errorMessage = getKey(`auth.errors.${errorCode}`);
          } else if (errorMessages?.[0]) {
            errorMessage = errorMessages?.[0];
          }

          setError(errorMessage);
        } else if (result.payload?.requireTwoFactorVerification) {
          mfaParams.current = result.payload;

          navigation.navigate('MFAScreen', result.payload);
        }
      }
      if (login.fulfilled.match(result)) {
        try {
          if (Platform.OS === 'android' && email !== prefilledEmail) {
            type BettyCredentialsModule = {
              savePassword: (u: string, p: string) => Promise<void>;
            };
            const { BettyCredentials } = NativeModules as {
              BettyCredentials?: BettyCredentialsModule;
            };
            if (BettyCredentials) {
              dispatch(startActivity());

              await BettyCredentials.savePassword(email, password);
            }
          }
        } catch {
          // no-op
        }
      }
    },
    [
      isEligible,
      email,
      password,
      error,
      dispatch,
      showError,
      navigation,
      prefilledEmail,
    ],
  );

  useEffect(() => {
    const tryPrefill = async () => {
      try {
        if (Platform.OS === 'android') {
          type BettyCredentialsModule = {
            getSavedPassword: () => Promise<{
              username: string;
              password: string;
            }>;
          };
          const { BettyCredentials } = NativeModules as {
            BettyCredentials?: BettyCredentialsModule;
          };
          if (BettyCredentials) {
            const creds = await BettyCredentials.getSavedPassword();
            if (creds?.username && creds?.password) {
              setEmail(creds.username);
              setPassword(creds.password);
              setPrefilledEmail(creds.username);
              return;
            }
          }
        }
      } catch {
        // ignore prefill errors
      }
      emailRef.current?.focus();
    };
    if (transitionEnded) {
      tryPrefill();
    }
  }, [transitionEnded]);

  const onForgotPasswordPress = useCallback(() => {
    navigation.navigate('ForgotPasswordScreen');
  }, [navigation]);

  const onRegisterPress = useCallback(() => {
    if (Platform.OS === 'web') {
      navigation.navigate('RegistrationScreen');
    } else {
      navigation.dispatch(StackActions.replace('RegistrationScreen'));
    }
  }, [navigation]);

  const onEmailSubmit = useCallback(() => {
    passwordRef.current?.focus();
  }, []);

  return (
    <Screen paddingBottom="safe-area-inset-bottom">
      <Box flex={1} justifyContent="space-between">
        <Box gap="space-600">
          <BettyIgoLogo marginTop="space-300" />
          <Box gap="space-600">
            <Input
              ref={emailRef}
              placeholderKey="email"
              placeholderNS="auth"
              autoComplete="username"
              keyboardType="email-address"
              textContentType="emailAddress"
              autoCapitalize="none"
              autoCorrect={false}
              value={email}
              onChangeText={setEmail}
              enterKeyHint="next"
              onSubmitEditing={onEmailSubmit}
              importantForAutofill="yes"
              error={error}
            />
            <Input
              ref={passwordRef}
              placeholderKey="password"
              placeholderNS="auth"
              secureTextEntry
              autoComplete="current-password"
              textContentType="password"
              value={password}
              autoCorrect={false}
              autoCapitalize="none"
              onChangeText={setPassword}
              onSubmitEditing={() => onLoginPress()}
              enterKeyHint="go"
              importantForAutofill="yes"
              error={error}
            />
            {error ? (
              <Text color="darkSurfaceError" variant="body-m">
                {error}
              </Text>
            ) : null}
            <Box
              flexDirection="row"
              gap="space-200"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap-reverse"
            >
              <Checkbox
                flex={1}
                flexWrap="wrap"
                error={showError && !isEligible}
                checked={isEligible}
                onChange={setIsEligible}
              >
                <Text variant="body-s" color="darkSurfacePrimary">
                  <Trans
                    i18nKey="eligibility"
                    ns="auth"
                    components={eligibilityComponents}
                  />
                </Text>
              </Checkbox>
              <Text
                variant="link-small"
                color="darkSurfacePrimary"
                onPress={onForgotPasswordPress}
                locKey="forgotPassword"
                locNS="auth"
              />
            </Box>
            {Config.RECAPTCHA_KEY && transitionEnded ? (
              <Recaptcha ref={recaptchaRef} />
            ) : null}
          </Box>
        </Box>
        <Box>
          <Box gap="space-600">
            <Button
              size="large"
              locKey="login"
              locNS="auth"
              onPress={() => onLoginPress()}
              disabled={showError && !isEligible}
              loading={loginDataState === 'pending' || recaptchaLoading}
            />
            <Box justifyContent="center" flexDirection="row" gap="space-050">
              <Text
                variant="body-s-semi"
                color="darkSurfacePrimary"
                locNS="auth"
                locKey="newToBetty"
              />
              <Text
                variant="link-small"
                color="darkSurfacePrimary"
                onPress={onRegisterPress}
                locNS="auth"
                locKey="signup"
              />
            </Box>
          </Box>
          <KeyboardSpacer isInsideSafeAreaView additionalOffset={-30} />
        </Box>
      </Box>
    </Screen>
  );
}

export default LoginScreen;
